import type { Metadata } from 'next';
import { getSocialMetadata } from '@/lib/metadata/utils';
import type { FloorPlanName } from '../types';

/*
 * Named Floor Plans Metadata
 */
export function buildNamedFloorPlanMetadataTitle(
  namedFloorPlan: FloorPlanName,
) {
  return `${namedFloorPlan} Floor Plan | Custom Home Design by Cornerstone Homes`;
}

export function buildNamedFloorPlanMetadataDesc(namedFloorPlan: FloorPlanName) {
  return `Discover the ${namedFloorPlan} by Cornerstone Homes, a custom floor plan featuring open-concept living, spacious bedrooms, and a modern kitchen. Perfect for those looking for personalized home design and quality craftsmanship`;
}

export function buildNamedFloorPlanMetaData(
  namedFloorPlan: FloorPlanName,
): Metadata {
  const title = buildNamedFloorPlanMetadataTitle(namedFloorPlan);
  const description = buildNamedFloorPlanMetadataDesc(namedFloorPlan);

  return {
    description,
    title,
    keywords: [
      `${namedFloorPlan} floor plan`,
      `${namedFloorPlan} custom home plan`,
      'custom floor plans Oklahoma',
      'home floor plans Claremore OK',
      'custom home floor plans Rogers County',
      'Cornerstone Homes floor plans',
      'new home floor plan Oklahoma',
    ],
    ...getSocialMetadata({
      title,
      description,
      url: `/floor-plans/${namedFloorPlan.toLowerCase().replace(/ /g, '-')}`,
    }),
  };
}

/*
 * Named Floor Plan Descriptions
 */
type FloorPlanHeroDescription = Record<FloorPlanName, string>;

export const FLOOR_PLAN_HERO_DESCRIPTIONS: FloorPlanHeroDescription = {
  Hyacinth: `Charming simplicity meets functional design in this traditional-style home. With 1,200 sqft of thoughtfully crafted space, enjoy an open-concept living and kitchen area, perfect for creating lasting memories. Featuring 3 cozy bedrooms and 2 baths, it's a timeless classic for modern living.`,
  Sage: `Sleek, stylish, and contemporary, this 1,450 sqft modern home offers 4 bedrooms and 2 baths. Clean lines and innovative design create a perfect sanctuary for those who appreciate understated elegance.`,
  Harmony: `Balance and beauty come together in this 1,600 sqft open-concept home. Featuring 3 bedrooms, 2 baths, and a seamless flow between the kitchen and living areas, this plan is perfect for effortless entertaining and comfortable living.`,
  Serenity: `Step into farmhouse charm with this 1,875 sqft versatile plan. Offering 3 bedrooms, 2 baths, and two floorplan options, it features a walk-in pantry and a spacious mudroom, making this home both beautiful and functional.`,
  Kingston: `Refined elegance meets practicality in this 2,030 sqft masterpiece. With 3 bedrooms, 2 baths, a private office, a butler's pantry, and an open-concept living and kitchen area, this home is tailored for those who love modern sophistication.`,
  Redbud: `Rooted in ranch-style charm, this 2,075 sqft farmhouse plan boasts 3 bedrooms, 2 baths, and a private office. Its open-concept design offers endless possibilities for comfort and style.`,
  'Country Living': `Experience the best of country charm with 2,230 sqft of thoughtfully designed space. This plan includes 4 bedrooms, 3.5 baths, an office, a mudroom, and a large inviting porch – perfect for enjoying serene landscapes.`,
  Mulberry: `Spacious and sophisticated, this 2,309 sqft home features 3 bedrooms, 3 baths, a private office, a mudroom, and a luxurious main suite. A walk-in pantry and thoughtful design make it an entertainer's dream.`,
  Birch: `Versatility and comfort define this expansive 2,723 sqft home. With 5 bedrooms, 3 baths, 2 laundry spaces, and an office, the unfinished upstairs area provides endless possibilities, from a game room to a private retreat.`,
  Willow: `Elegant and timeless, this 2,811 sqft home offers 3 bedrooms, 3.5 baths, an office, and a formal dining room. The luxurious main suite and seasonal-height walk-in closet make this plan truly exceptional.`,
  Hickory: `Stately and spacious, this 2,990 sqft home features 3 bedrooms, 2.5 baths, an office, a 3-car garage, and an oversized living room with a fireplace. A 4-bedroom, 3-bath option adds even more flexibility to this refined design.`,
  Elderberry: `Unmatched luxury in a stunning transitional style. At 3,870 sqft, this home offers 5 bedrooms, 5.5 baths, an office, a mudroom, a seasonal-height closet, a walk-in pantry, and a spacious game room over the garage. Perfect for those who demand the very best.`,
  'River Ranch': `Classic ranch-style living with 2,000 sqft of open and inviting space. This thoughtfully designed home features 3 bedrooms and 2 baths, blending comfort and practicality. With a seamless flow between the living, dining, and kitchen areas, it's the perfect retreat for relaxed country living.`,
};
